import { useCallback, useEffect, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { BsCloudUpload } from 'react-icons/bs';

import Alert from '#globalComponents/Alert/Alert';
import Button from '#globalComponents/Button/Button';
import Modal from '#globalComponents/Overlay/Modal';
import Panel from '#globalComponents/Overlay/Panel';
import { useError } from '#globalContexts/ErrorContext';

import { companiesKeys } from '../../models/companies/keys';
import { contactsKeys } from '../../models/contacts/keys';
import { dealsKeys } from '../../models/deals/keys';
import { useAnalyzePipedriveImport, useStartPipedriveImport } from '../../models/imports/mutations';
import { usePipedriveJobPolling } from '../../models/imports/queries';

import PipedriveResultStep from './steps/PipedriveResultStep';

const STEPS = { UPLOAD: 1, PREVIEW: 2, RESULT: 3 };
const STEP_LABELS = [
	{ step: STEPS.UPLOAD, label: 'Dateien' },
	{ step: STEPS.PREVIEW, label: 'Vorschau' },
	{ step: STEPS.RESULT, label: 'Import' },
];
const TERMINAL = ['completed', 'failed'];
const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20 MB per file

const ENTITY_LABELS = {
	organizations: 'Organisationen → Firmen',
	people: 'Personen → Kontakte',
	deals: 'Deals',
	activities: 'Aktivitäten',
	notes: 'Notizen',
};

export default function PipedriveImportWizard({ isOpen, onClose }) {
	const queryClient = useQueryClient();
	const { showError } = useError();

	const [currentStep, setCurrentStep] = useState(STEPS.UPLOAD);
	const [analysis, setAnalysis] = useState(null);
	const [importResult, setImportResult] = useState(null);
	const [asyncJobId, setAsyncJobId] = useState(null);

	const analyzeMutation = useAnalyzePipedriveImport();
	const startMutation = useStartPipedriveImport();
	const jobQuery = usePipedriveJobPolling(asyncJobId, { enabled: Boolean(asyncJobId) });

	const invalidateCrm = useCallback(() => {
		for (const key of [contactsKeys.all, companiesKeys.all, dealsKeys.all]) {
			void queryClient.invalidateQueries({ queryKey: key });
		}
	}, [queryClient]);

	const handleFiles = useCallback(
		async (fileList) => {
			const files = [...fileList].filter((f) => f.name.toLowerCase().endsWith('.csv') && f.size <= MAX_FILE_SIZE);
			if (files.length === 0) {
				showError({ title: 'Keine gültigen CSV-Dateien', error: { code: 'CRM_PIPEDRIVE_NO_FILES' } });
				return;
			}
			try {
				const result = await analyzeMutation.mutateAsync(files);
				setAnalysis(result);
				setCurrentStep(STEPS.PREVIEW);
			} catch (err) {
				showError({ title: 'Analyse fehlgeschlagen', error: err });
			}
		},
		[analyzeMutation, showError],
	);

	const handleStart = useCallback(async () => {
		setCurrentStep(STEPS.RESULT);
		try {
			const result = await startMutation.mutateAsync(analysis.batchId);
			setAsyncJobId(result.jobId);
		} catch (err) {
			showError({ title: 'Migration fehlgeschlagen', error: err });
			setImportResult({ status: 'failed', summary: {}, errors: [] });
		}
	}, [analysis, startMutation, showError]);

	const asyncStatus = jobQuery.data?.status;
	useEffect(() => {
		if (asyncJobId && asyncStatus && TERMINAL.includes(asyncStatus)) {
			setImportResult(jobQuery.data);
			invalidateCrm();
			setAsyncJobId(null);
		}
	}, [asyncJobId, asyncStatus, jobQuery.data, invalidateCrm]);

	const handleClose = useCallback(() => {
		setCurrentStep(STEPS.UPLOAD);
		setAnalysis(null);
		setImportResult(null);
		setAsyncJobId(null);
		onClose();
	}, [onClose]);

	const effectiveResult = importResult ?? (asyncJobId ? jobQuery.data : null);
	const isProcessing = startMutation.isPending || (Boolean(asyncJobId) && jobQuery.data?.status === 'processing');

	return (
		<Modal isOpen={isOpen} onClose={handleClose} size="l">
			<Panel.Header onClose={handleClose}>Aus Pipedrive migrieren</Panel.Header>

			<StepIndicator currentStep={currentStep} />

			<Panel.Body className="min-h-[400px]">
				{currentStep === STEPS.UPLOAD && (
					<UploadStep isAnalyzing={analyzeMutation.isPending} onFiles={handleFiles} onCancel={handleClose} />
				)}
				{currentStep === STEPS.PREVIEW && analysis && (
					<PreviewStep
						analysis={analysis}
						isStarting={startMutation.isPending}
						onConfirm={handleStart}
						onBack={() => setCurrentStep(STEPS.UPLOAD)}
					/>
				)}
				{currentStep === STEPS.RESULT && (
					<PipedriveResultStep
						result={effectiveResult}
						isLoading={isProcessing}
						totalRows={analysis?.totalRows ?? 0}
						onClose={handleClose}
					/>
				)}
			</Panel.Body>
		</Modal>
	);
}

function UploadStep({ isAnalyzing, onFiles, onCancel }) {
	const [dragActive, setDragActive] = useState(false);

	if (isAnalyzing) {
		return (
			<div className="flex flex-col items-center justify-center py-16">
				<div className="w-12 h-12 border-4 border-primary-200 border-t-primary-500 rounded-full animate-spin mb-4" />
				<p className="text-md font-medium text-text">Dateien werden analysiert...</p>
			</div>
		);
	}

	return (
		<div>
			<p className="text-sm text-text mb-4">
				Laden Sie alle Pipedrive-Export-Dateien gleichzeitig hoch (Organisationen, Personen, Deals, Aktivitäten,
				Notizen). Die Verknüpfungen zwischen den Datensätzen werden automatisch übernommen.
			</p>
			<div
				className={`border-2 border-dashed rounded-card p-12 text-center transition-colors cursor-pointer ${
					dragActive ? 'border-primary-400 bg-primary-100' : 'border-bgBase-600 hover:border-bgBase-700'
				}`}
				onDragOver={(e) => {
					e.preventDefault();
					setDragActive(true);
				}}
				onDragLeave={() => setDragActive(false)}
				onDrop={(e) => {
					e.preventDefault();
					setDragActive(false);
					onFiles(e.dataTransfer?.files ?? []);
				}}
				onClick={() => document.getElementById('pipedrive-file-input')?.click()}
			>
				<BsCloudUpload className="w-12 h-12 text-bgBase-800 mx-auto mb-4" />
				<p className="text-md font-medium text-text">Pipedrive-CSVs hierher ziehen oder klicken</p>
				<p className="text-sm text-bgBase-800 mt-1">Mehrere Dateien gleichzeitig, je max. 20 MB</p>
				<input
					id="pipedrive-file-input"
					type="file"
					accept=".csv"
					multiple
					className="hidden"
					onChange={(e) => onFiles(e.target.files ?? [])}
				/>
			</div>
			<div className="flex justify-between mt-6">
				<Button variant="outline" onClick={onCancel}>
					Abbrechen
				</Button>
			</div>
		</div>
	);
}

function PreviewStep({ analysis, isStarting, onConfirm, onBack }) {
	const { recognised = [], ignored = [], totalRows = 0, pipelinesPreview = [] } = analysis;

	return (
		<div>
			<p className="text-sm text-text mb-4">
				{recognised.length} Datei(en) erkannt, {totalRows.toLocaleString('de-DE')} Zeilen insgesamt.
			</p>

			<div className="border border-bgBase-500 rounded-card overflow-hidden mb-4">
				<table className="w-full text-sm">
					<tbody>
						{recognised.map((r) => (
							<tr key={r.entityType} className="border-b border-bgBase-400 last:border-0">
								<td className="px-4 py-2 font-medium text-text">
									{ENTITY_LABELS[r.entityType] ?? r.entityType}
								</td>
								<td className="px-4 py-2 text-bgBase-800">{r.fileName}</td>
								<td className="px-4 py-2 text-right text-text">
									{r.rowCount.toLocaleString('de-DE')} Zeilen
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>

			{pipelinesPreview.length > 0 && (
				<div className="mb-4 text-sm text-text">
					<span className="font-medium text-text">Neue Pipelines:</span>{' '}
					{pipelinesPreview.map((p) => `${p.name} (${p.phaseCount} Phasen)`).join(', ')}
				</div>
			)}

			{ignored.length > 0 && (
				<div className="mb-4 text-xs text-bgBase-800">
					Ignoriert: {ignored.map((i) => i.fileName).join(', ')} (nicht unterstützt)
				</div>
			)}

			<Alert tone="caution" className="p-3">
				Dies ist ein einmaliger Migrations-Import. Ein erneuter Lauf mit denselben Dateien würde Duplikate
				anlegen.
			</Alert>

			<div className="flex justify-between mt-6">
				<Button variant="outline" onClick={onBack} disabled={isStarting}>
					Zurück
				</Button>
				<Button onClick={onConfirm} disabled={isStarting}>
					Migration starten
				</Button>
			</div>
		</div>
	);
}

function StepIndicator({ currentStep }) {
	return (
		<div className="flex items-center px-5 py-3.5 border-b border-bgBase-400 flex-shrink-0">
			{STEP_LABELS.map(({ step, label }, index) => (
				<div key={step} className="flex items-center">
					<div
						className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
							currentStep >= step ? 'bg-primary-500 text-white' : 'bg-bgBase-400 text-text'
						}`}
					>
						{index + 1}
					</div>
					<span className={`ml-2 text-sm ${currentStep >= step ? 'text-text' : 'text-bgBase-800'}`}>
						{label}
					</span>
					{index < STEP_LABELS.length - 1 && (
						<div className={`w-12 h-0.5 mx-3 ${currentStep > step ? 'bg-primary-500' : 'bg-bgBase-400'}`} />
					)}
				</div>
			))}
		</div>
	);
}

PipedriveImportWizard.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
};

UploadStep.propTypes = {
	isAnalyzing: PropTypes.bool,
	onFiles: PropTypes.func.isRequired,
	onCancel: PropTypes.func.isRequired,
};

PreviewStep.propTypes = {
	analysis: PropTypes.object.isRequired,
	isStarting: PropTypes.bool,
	onConfirm: PropTypes.func.isRequired,
	onBack: PropTypes.func.isRequired,
};

StepIndicator.propTypes = {
	currentStep: PropTypes.number.isRequired,
};
